/**
 * Creative grid card (Creations library, Studio results). Thumbnail hover-plays video; status pill,
 * kind/aspect chips, name, hook line, trigger/stage chips and age. Optional `footer` slot (e.g. inline
 * ApprovalActions in Studio).
 */
import { Download, Film, Image as ImageIcon, Sparkles, Upload } from "lucide-react";
import type { ReactNode } from "react";
import { creativeSource, type Creative } from "@/api/types";
import { cn } from "@/lib/cn";
import { humanize, relative } from "@/lib/format";
import { Pill } from "../ui/Badge";
import { Skeleton } from "../ui/Feedback";
import { CreativeMedia, CreativeStatusPill } from "./CreativeBits";
import { creativeAspects, creativePreview, creativeQa } from "./normalize";

export const SOURCE_META: Record<string, { label: string; icon: ReactNode }> = {
  generated: { label: "Generated", icon: <Sparkles className="size-3" strokeWidth={1.75} /> },
  uploaded: { label: "Uploaded", icon: <Upload className="size-3" strokeWidth={1.75} /> },
  imported: { label: "Imported", icon: <Download className="size-3" strokeWidth={1.75} /> },
};

export function CreativeCard({ creative, onOpen, footer, className, compact }: { creative: Creative; onOpen?: (c: Creative) => void; footer?: ReactNode; className?: string; compact?: boolean }) {
  const p = creativePreview(creative);
  const aspects = creativeAspects(creative);
  const src = SOURCE_META[creativeSource(creative)];
  const qa = creativeQa(creative);
  return (
    <article
      className={cn(
        "group flex flex-col overflow-hidden rounded-card border border-line bg-surface transition-[border-color,transform,box-shadow] duration-200 hover:-translate-y-0.5 hover:border-line-strong hover:shadow-pop",
        className,
      )}
    >
      <button type="button" onClick={() => onOpen?.(creative)} className="relative block w-full text-left" aria-label={`Open ${creative.name}`}>
        <div className={cn("relative w-full overflow-hidden bg-[#141416]", compact ? "aspect-square" : "aspect-[4/5]")}>
          <CreativeMedia url={p.url} poster={p.poster} mediaType={p.mediaType} className="size-full transition-transform duration-500 group-hover:scale-[1.03]" alt={creative.name} />
          <div className="pointer-events-none absolute inset-x-0 top-0 flex items-start justify-between gap-2 bg-gradient-to-b from-black/55 to-transparent p-2.5">
            <CreativeStatusPill status={creative.status} size="xs" className="backdrop-blur-sm" />
            <span className="flex items-center gap-1">
              {qa === "fail" && (
                <Pill tone="danger" size="xs" className="backdrop-blur-sm">
                  QA
                </Pill>
              )}
              <span className="inline-flex h-5 items-center gap-1 rounded-full bg-black/55 px-1.5 text-[10.5px] font-medium text-white/90 backdrop-blur-sm">
                {creative.kind === "video" ? <Film className="size-3" strokeWidth={1.75} /> : <ImageIcon className="size-3" strokeWidth={1.75} />}
                {aspects.length ? aspects.join(" · ") : humanize(creative.kind)}
              </span>
            </span>
          </div>
          {typeof creative.generation === "number" && creative.generation > 0 && (
            <span className="pointer-events-none absolute bottom-2 left-2 rounded-full bg-black/60 px-1.5 py-0.5 text-[10px] font-semibold text-white/90 backdrop-blur-sm">
              Gen {creative.generation}
            </span>
          )}
        </div>
      </button>
      <div className="flex flex-1 flex-col gap-2 p-3">
        <button type="button" onClick={() => onOpen?.(creative)} className="min-w-0 text-left">
          <div className="truncate text-[13.5px] font-semibold text-fg group-hover:text-white">{creative.name}</div>
          {creative.hook && !compact && <p className="mt-0.5 line-clamp-2 text-[12.5px] leading-snug text-muted">“{creative.hook}”</p>}
        </button>
        {!compact && (creative.emotionalTrigger || creative.awarenessStage) && (
          <div className="flex flex-wrap gap-1">
            {creative.emotionalTrigger && (
              <Pill tone="primary" size="xs">
                {humanize(String(creative.emotionalTrigger))}
              </Pill>
            )}
            {creative.awarenessStage && (
              <Pill tone="info" size="xs">
                {humanize(String(creative.awarenessStage))}
              </Pill>
            )}
          </div>
        )}
        <div className="mt-auto flex items-center justify-between gap-2 pt-0.5 text-[11.5px] text-subtle">
          <span className="inline-flex items-center gap-1">
            {src?.icon}
            {src?.label}
            {creative.format ? <span className="text-subtle/80"> · {humanize(String(creative.format))}</span> : null}
          </span>
          <span className="tnum">{relative(creative.createdAt)}</span>
        </div>
        {footer && <div className="border-t border-line pt-2.5">{footer}</div>}
      </div>
    </article>
  );
}

export function CreativeCardSkeleton({ compact }: { compact?: boolean }) {
  return (
    <div className="overflow-hidden rounded-card border border-line bg-surface">
      <Skeleton className={cn("w-full rounded-none", compact ? "aspect-square" : "aspect-[4/5]")} />
      <div className="flex flex-col gap-2 p-3">
        <Skeleton className="h-3.5 w-3/4" />
        {!compact && <Skeleton className="h-3 w-full" />}
        {!compact && <Skeleton className="h-3 w-2/5" />}
      </div>
    </div>
  );
}
